import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

/** What a page puts in the app header; each is cleared when the page leaves. */
export type SecureHeaderSlots = {
  /** Replaces the default icon and title. */
  headerContent: ReactNode | null;
  /** Next to the title: a switch between the page's own views. */
  headerAddon: ReactNode | null;
  /** At the header's end: stepping through records, closing a record. */
  headerActions: ReactNode | null;
};

type SecureHeaderContextValue = SecureHeaderSlots & {
  setHeaderContent: (content: ReactNode | null) => void;
  setHeaderAddon: (content: ReactNode | null) => void;
  setHeaderActions: (content: ReactNode | null) => void;
};

const SecureHeaderContext = createContext<SecureHeaderContextValue>({
  headerContent: null,
  headerAddon: null,
  headerActions: null,
  setHeaderContent: () => {},
  setHeaderAddon: () => {},
  setHeaderActions: () => {},
});

export function SecureHeaderProvider({ children }: { children: ReactNode }) {
  const [headerContent, setHeaderContent] = useState<ReactNode | null>(null);
  const [headerAddon, setHeaderAddon] = useState<ReactNode | null>(null);
  const [headerActions, setHeaderActions] = useState<ReactNode | null>(null);

  return (
    <SecureHeaderContext.Provider
      value={{
        headerContent,
        headerAddon,
        headerActions,
        setHeaderContent,
        setHeaderAddon,
        setHeaderActions,
      }}
    >
      {children}
    </SecureHeaderContext.Provider>
  );
}

export function useSecureHeader() {
  return useContext(SecureHeaderContext);
}
